import { useMemo } from "react";

import { BackendType } from "../models/financing";
import { IAPR, IMonths, isoAPR, isoMonths } from "../models/nominals";
import { Dinero, getDinero } from "./money";
import { getPageSetting, usePageSetting } from "./settings";

export interface FinancingPlanMeta {
    type?: string;
    threshold: Dinero;
    length: IMonths;
    apr: IAPR;
    superscript: string;
    description: string;
    default?: boolean;
}

type RawAdvertisingPlan = NonNullable<
    ReturnType<typeof getPageSetting<"financing_advertising_plans">>
>[number];

const transformAdvertisingPlans = (
    advertisingPlans: readonly RawAdvertisingPlan[],
): readonly FinancingPlanMeta[] =>
    advertisingPlans
        .map((plan) => {
            return {
                // Defensive optional chain: types say backend_type is
                // non-null, but a misconfigured Django serializer could
                // produce null. On the SSR path an unhandled TypeError
                // here would crash the entire page response, so we degrade
                // to `undefined` and filter the plan out below.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                type: plan.backend_type?.code,
                threshold: getDinero(plan.product_price_threshold),
                length: isoMonths.wrap(plan.term_months),
                apr: isoAPR.wrap(plan.apr),
                superscript: plan.fine_print_superscript,
                description: plan.description,
                default: plan.is_default_plan,
            };
        })
        // Drop plans whose backend_type was null — they can't be routed
        // to a financing backend, so surfacing them via downstream
        // selectors (getDefaultFinancingPlan, getLowestThresholdPlan,
        // etc.) would produce broken financing UI. The type guard mirrors
        // the defensive optional chain above: TypeScript believes `type`
        // is always set (backend_type is typed non-null), but a
        // misconfigured serializer could prove it wrong at runtime.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        .filter((plan) => plan.type !== undefined)
        .sort((a, b) => {
            if (a.threshold.equalsTo(b.threshold)) {
                return 0;
            }
            return a.threshold.lessThan(b.threshold) ? -1 : 1;
        });

/**
 * List the currently available Financing plans.
 *
 * Reads from window/DOM (getPageSetting) — only safe on the client after
 * hydration. For SSR-safe access, use the `useFinancingPlans` hook instead.
 */
export const listFinancingPlans = (): FinancingPlanMeta[] => {
    const advertisingPlans =
        getPageSetting("financing_advertising_plans") || [];
    // Return a mutable copy so callers are free to sort or mutate it.
    return [...transformAdvertisingPlans(advertisingPlans)];
};

/**
 * Hook variant of `listFinancingPlans`. Reads from the Reactivated React
 * context via `usePageSetting`, so it works during SSR and client renders.
 */
export const useFinancingPlans = (): readonly FinancingPlanMeta[] => {
    const advertisingPlans = usePageSetting("financing_advertising_plans");
    return useMemo(
        () => transformAdvertisingPlans(advertisingPlans ?? []),
        [advertisingPlans],
    );
};

/**
 * Returns the Wells Fargo hosted credit-application URL when the site is
 * configured for hosted mode (see HostedFinancingSettings in tsi-common),
 * else null. When set, financing CTAs link out to this URL instead of
 * opening the in-site application modal.
 */
export const useHostedApplicationUrl = (): string | null => {
    const url = usePageSetting("hosted_application_url");
    return url || null;
};

/**
 * List filtered Financing plans.
 */
const listFilteredFinancingPlans = (
    backendType: BackendType,
): FinancingPlanMeta[] => {
    const plans = listFinancingPlans();
    const filteredPlans = plans.filter(
        (plan) => plan.type?.toLowerCase() === backendType,
    );
    return filteredPlans;
};

export const getDefaultFinancingPlan = (): FinancingPlanMeta | null => {
    const plans = listFinancingPlans();
    return plans.find((plan) => plan.default) ?? null;
};

export const getLowestThresholdPlan = (): FinancingPlanMeta | null => {
    const plans = listFinancingPlans();
    return plans.length > 0 ? plans[0] : null;
};

export const getLowestThresholdFortivaPlan = (
    backendType: BackendType,
): FinancingPlanMeta | null => {
    const filteredPlans = listFilteredFinancingPlans(backendType);
    return filteredPlans.length > 0 ? filteredPlans[0] : null;
};

export const getBestEligiblePlan = (
    amount: Dinero,
): FinancingPlanMeta | null => {
    const plans = listFinancingPlans().filter((plan) => {
        return plan.threshold.lessThanOrEqual(amount);
    });
    plans.sort((a, b) => {
        if (a.length === b.length) {
            return 0;
        }
        return a.length < b.length ? 1 : -1;
    });
    return plans.length > 0 ? plans[0] : null;
};
